import { z } from 'zod';

import { ApiError } from '../api/client';

const timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Hora inválida, formato HH:mm');

/**
 * Mirrors `apps/api` `createSiteSchema` (`customer-site.dto.ts`): the
 * billing-address-style "both or neither" rule for the delivery window, and
 * start strictly before end (`customer-sites/spec.md` → "Ventana horaria de
 * recepción").
 */
const addressGroupSchema = z.object({
  street: z.string().trim().max(200),
  number: z.string().trim().max(20),
  city: z.string().trim().max(120),
  province: z.string().trim().max(120),
  postalCode: z.string().trim().max(20),
});

export const siteFormSchema = z
  .object({
    name: z.string().trim().min(1, 'El nombre es obligatorio').max(200),
    address: addressGroupSchema,
    latitude: z.string().trim(),
    longitude: z.string().trim(),
    deliveryWindowStart: z.string().trim(),
    deliveryWindowEnd: z.string().trim(),
    receptionInstructions: z.string().trim().max(2000),
    defaultReceiverName: z.string().trim().max(200),
    contactPhone: z.string().trim().max(50),
  })
  .superRefine((data, ctx) => {
    const addressValues = Object.entries(data.address);
    const anyAddressFilled = addressValues.some(([, value]) => value !== '');

    if (anyAddressFilled) {
      for (const [field, value] of addressValues) {
        if (value === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Completá este campo o dejá vacía toda la dirección',
            path: ['address', field],
          });
        }
      }
    }

    const { deliveryWindowStart: start, deliveryWindowEnd: end } = data;

    if (start !== '' || end !== '') {
      if (start === '' || end === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Completá ambas horas o dejá la ventana vacía',
          path: ['deliveryWindowEnd'],
        });
      } else if (!timeSchema.safeParse(start).success || !timeSchema.safeParse(end).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Hora inválida, formato HH:mm',
          path: ['deliveryWindowEnd'],
        });
      } else if (start >= end) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'La hora de inicio debe ser anterior a la hora de fin',
          path: ['deliveryWindowEnd'],
        });
      }
    }

    if ((data.latitude === '') !== (data.longitude === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Completá latitud y longitud juntas',
        path: ['longitude'],
      });
    } else if (data.latitude !== '' && data.longitude !== '') {
      const lat = Number(data.latitude);
      const lng = Number(data.longitude);

      if (Number.isNaN(lat) || lat < -90 || lat > 90) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Latitud inválida',
          path: ['latitude'],
        });
      }

      if (Number.isNaN(lng) || lng < -180 || lng > 180) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Longitud inválida',
          path: ['longitude'],
        });
      }
    }
  });

export type SiteFormValues = z.infer<typeof siteFormSchema>;

export const EMPTY_SITE_ADDRESS = {
  street: '',
  number: '',
  city: '',
  province: '',
  postalCode: '',
};

export const DEFAULT_SITE_FORM_VALUES: SiteFormValues = {
  name: '',
  address: EMPTY_SITE_ADDRESS,
  latitude: '',
  longitude: '',
  deliveryWindowStart: '',
  deliveryWindowEnd: '',
  receptionInstructions: '',
  defaultReceiverName: '',
  contactPhone: '',
};

/**
 * Server errors never have their `message` parsed: `code` on the 409/403
 * body (`CustomersExceptionFilter`) drives the mapping, same convention as
 * `customer-form-schema.ts`'s `mapCreateError`.
 */
export function mapSiteError(error: unknown): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 409 && code === 'SITE_DEFAULT_PROTECTED') {
    return 'Asigná otra sede por defecto antes de desactivar o eliminar esta.';
  }

  if (error.status === 409 && code === 'SITE_INACTIVE') {
    return 'Una sede inactiva no puede ser la sede por defecto.';
  }

  if (error.status === 409 && code === 'SITE_DEFAULT_CONFLICT') {
    return 'Otra sede quedó marcada como sede principal en simultáneo. La lista se actualizó.';
  }

  if (error.status === 403 && code === 'SITE_SCOPE_RESTRICTED') {
    return 'No tenés permiso para realizar esta acción sobre esta sede.';
  }

  if (error.status === 404) {
    return 'La sede ya no existe.';
  }

  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 400) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}

/** Codes whose recovery is "refetch the list", not just "show the message". */
export function shouldRefetchOnError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }

  const code = (error.body as { code?: string } | null)?.code;

  return error.status === 404 || (error.status === 409 && code === 'SITE_DEFAULT_CONFLICT');
}
